import * as React from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import { Box, Chip, Typography } from '@mui/material';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import {
    ContextActionType,
    EmptyBox,
    HeaderActionButton,
    HeaderListSkeleton,
    ListHeader,
    ListSkeleton,
    SkeletonLoader,
    useDumbActionsContext,
    useModal,
} from '@europrocurement/flexy-components';
import {
    ColumnDatatable,
    FiltersDatatableList,
    StoreDatatable,
} from '@europrocurement/flexy-datatable';
import {
    antennesSelectorMain,
    AntennesUtilisateurApiObject,
    useUtilisateurService,
    useUtilisateursServiceSelector,
    UtilisateursAppDispatch,
} from '@europrocurement/l2d-domain';
import { customizerSelector } from '@europrocurement/flexy-components/redux/storeConfig/selectors';
import { PossibleTypes, useSearchBar } from '@europrocurement/l2d-modules';
import { FlexyForm } from '@europrocurement/flexy-form';
import {
    errorNotification,
    successNotification,
} from '@europrocurement/flexy-components/components/atoms/NotificationToasters';
import { FaOptionIcon, plusIcon } from '@europrocurement/l2d-icons';

import { useClickActions, useInitialLoadingState } from '@europrocurement/l2d-hooks';
import { useAppSelector } from '../../../../redux';
import { getAntennaCreationFormStructure } from '../../components/Antenne/antennaFormStructure';

export type AntennaType = {
    idAntenne?: number;
    libelle?: string | null;
    isDeleted: boolean;
};

type AntennaCreationType = {
    libelle?: string | null;
};

const filters: FiltersDatatableList = [
    {
        label: 'Supprimé',
        field: 'isDeleted',
        type: 'boolean',
    },
];

/**
 * Default values for the antenna creation form
 */
const defaultCreatedAntennaValues: AntennaCreationType = {
    libelle: '',
};

/**
 * Page de listing des antennes.
 * Affiche les antennes dans un StoreDatatable avec navigation vers la fiche détail.
 */
export const AntennesList: React.FunctionComponent = function () {
    const navigate = useNavigate();
    const dispatch = useDispatch<UtilisateursAppDispatch>();
    const { modalActions } = useModal();
    const { setIsSearchBarPath, setSearchBarType } = useSearchBar();

    const location = useLocation();
    const { xIdSociete } = useAppSelector(customizerSelector);
    const { getAntennes, antennesApi } = useUtilisateurService();

    const dataSource = useUtilisateursServiceSelector(antennesSelectorMain);
    const { isInitialLoading, isInError } = useInitialLoadingState(dataSource);
    const { navigateOnClick } = useClickActions({ navigate });

    // Activation du SearchBar pour les antennes
    React.useEffect(() => {
        setSearchBarType(PossibleTypes.Antenne);
        setIsSearchBarPath(true);
        return () => {
            setSearchBarType(undefined);
            setIsSearchBarPath(false);
        };
    }, [setIsSearchBarPath, setSearchBarType]);

    const breadcrumbPath = React.useMemo(
        () => [
            {
                link: '/accueil',
                icon: {
                    icon: ['fasl', 'house'] as IconProp,
                    sx: { display: 'flex', alignItems: 'center' },
                },
            },
            { link: '/utilisateurs/antennes', path: 'Antennes' },
            'Liste des antennes',
        ],
        [],
    );

    React.useEffect(() => {
        dispatch(getAntennes({}));
    }, [dispatch, getAntennes]);

    /**
     * Defines the columns in the StoreDatable render
     */
    const columns: ColumnDatatable<AntennesUtilisateurApiObject>[] = [
        {
            label: 'ID',
            render: ({ idAntenne }: AntennesUtilisateurApiObject) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {idAntenne}
                </Typography>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'idAntenne',
            sx: {
                width: '100px',
            },
        },
        {
            label: 'Libelle',
            render: ({ libelle }: AntennesUtilisateurApiObject) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {libelle}
                </Typography>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'libelle',
        },
        {
            label: 'Statut',
            isDisplayed: true,
            field: 'isDeleted',
            render: (antenne: AntennesUtilisateurApiObject) =>
                antenne.isDeleted ? (
                    <Chip label="Inactif" />
                ) : (
                    <Chip
                        label="Actif"
                        color="primary"
                    />
                ),
        },
    ];

    /**
     * On creation form submit, formates data and post them on the API.
     * Strikes success or error pop-up and get the new list of antennas for rendering.
     * @param formValues antenna creation form values
     */
    const handleCreateAntenna = React.useCallback(
        (formValues: AntennaType) => {
            const payload: AntennaCreationType = {
                libelle: formValues.libelle,
            };

            antennesApi
                .apiAntennesPost({ antennesJsonldAntenneWrite: payload })
                .then(() => {
                    successNotification("L'antenne a bien été créée.");
                    dispatch(getAntennes({}));
                })
                .catch(errorNotification);

            modalActions.close();
        },
        [antennesApi, dispatch, getAntennes, modalActions],
    );

    /**
     * Open the modal with the antenna creation form
     */
    const openCreateAntennaForm = React.useCallback(() => {
        modalActions.call(
            <FlexyForm
                formObject={defaultCreatedAntennaValues}
                formStructure={getAntennaCreationFormStructure()}
                onSubmit={handleCreateAntenna}
            />,
        );
    }, [handleCreateAntenna, modalActions]);

    // Chargement initial des données
    React.useEffect(() => {
        dispatch(getAntennes({}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleRowClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { idAntenne }: AntennesUtilisateurApiObject) => {
            navigateOnClick({ path: `${idAntenne}` });
        },
        [navigateOnClick],
    );

    const handleRowWheelClick = React.useCallback(
        (__: React.MouseEvent<HTMLElement>, { idAntenne }: AntennesUtilisateurApiObject) => {
            navigateOnClick({ path: `${location.pathname}/${idAntenne}`, openNewTab: true });
        },
        [location.pathname, navigateOnClick],
    );

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Nouvelle antenne',
                icon: <FaOptionIcon {...plusIcon.props} />,
                action: openCreateAntennaForm,
            },
        ],
        [openCreateAntennaForm],
    );

    useDumbActionsContext(contextActions);

    const errorContent = React.useMemo(
        () => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                }}
            >
                <EmptyBox
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'space-evenly',
                        height: '25%',
                        width: '50%',
                    }}
                    text="Une erreur s'est produite lors de la récupération des antennes."
                    subText="Veuillez recharger la page. Si le problème persiste, contactez votre administrateur."
                />
            </Box>
        ),
        [],
    );

    return (
        <SkeletonLoader
            isLoading={isInitialLoading}
            skeleton={
                <>
                    <HeaderListSkeleton />
                    <ListSkeleton columns={3} />
                </>
            }
            isInError={isInError}
            errorChildren={errorContent}
        >
            <ListHeader
                breadcrumbPath={breadcrumbPath}
                actions={
                    <HeaderActionButton
                        label="Nouvelle antenne"
                        icon={plusIcon}
                        onClick={openCreateAntennaForm}
                        actionType="modal"
                    />
                }
            />

            {dataSource && (
                <StoreDatatable
                    dataSource={dataSource}
                    columns={columns}
                    fetchData={getAntennes}
                    filters={filters}
                    onClickRow={handleRowClick}
                    onWheelClickRow={handleRowWheelClick}
                    observers={[xIdSociete]}
                />
            )}
        </SkeletonLoader>
    );
};
